import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Launch as LaunchIcon,
  Refresh as RefreshIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';
import { checkoutBlockReasons, getStoreAccessReasonKey } from '../../utils/storeAccessUtils';

const StoreAccessBlockedBanner = ({
  blockReason,
  isOwner,
  storeName,
  onOpenBillingPortal,
  billingPortalLoading,
  onStartCheckout,
  checkoutLoading,
  onRefresh,
  refreshing,
}) => {
  const { t } = useTranslation();
  const reasonKey = getStoreAccessReasonKey(blockReason);
  const title = t(`stores.access.reasons.${reasonKey}.title`);
  const description = t(`stores.access.reasons.${reasonKey}.description`);
  const badgeLabel = t(`stores.access.reasons.${reasonKey}.badge`);
  const hint = isOwner ? t('stores.access.ownerHint') : t('stores.access.nonOwnerHint');

  return (
    <Box
      sx={{
        width: '100%',
        mb: { xs: 2, md: 2.5 },
        px: { xs: 2, sm: 3, md: 4 },
        pt: { xs: 2, md: 2.5 },
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          borderRadius: BRAND_RADIUS.card,
          border: `1px solid ${BRAND.line}`,
          background: BRAND.surface,
          boxShadow: 'none',
          p: { xs: 2, sm: 2.5 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            alignItems: { xs: 'stretch', lg: 'flex-start' },
            justifyContent: 'space-between',
            gap: { xs: 2, lg: 3 },
          }}
        >
          <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0, flex: 1 }}>
            <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 28, flexShrink: 0, mt: 0.25 }} />
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip
                  label={badgeLabel}
                  size="small"
                  sx={{
                    borderRadius: BRAND_RADIUS.pill,
                    bgcolor: brandAlpha('accent', 0.12),
                    color: BRAND.accentText,
                    fontWeight: 700,
                  }}
                />
                {storeName ? (
                  <Typography variant="caption" sx={{ color: BRAND.textMuted, fontWeight: 700 }}>
                    {storeName}
                  </Typography>
                ) : null}
              </Box>

              <Typography
                variant="h6"
                sx={{
                  color: BRAND.text,
                  fontWeight: 800,
                  lineHeight: 1.2,
                  mb: 0.75,
                }}
              >
                {title}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: BRAND.textMuted,
                  lineHeight: 1.6,
                  mb: 0.75,
                }}
              >
                {description}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: BRAND.textMuted,
                  lineHeight: 1.55,
                }}
              >
                {hint}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              flexShrink: 0,
              alignSelf: { xs: 'stretch', lg: 'flex-start' },
            }}
          >
            {isOwner && checkoutBlockReasons.has(blockReason) ? (
              <PrimaryActionButton
                size="small"
                onClick={onStartCheckout}
                disabled={checkoutLoading}
                startIcon={checkoutLoading ? <CircularProgress size={16} color="inherit" /> : <LaunchIcon sx={{ fontSize: 18 }} />}
                sx={{ px: 2, py: 0.9, whiteSpace: 'nowrap' }}
              >
                {checkoutLoading ? t('stores.checkout.opening') : t('stores.checkout.action')}
              </PrimaryActionButton>
            ) : null}

            {isOwner ? (
              <Button
                size="small"
                variant={checkoutBlockReasons.has(blockReason) ? 'outlined' : 'contained'}
                onClick={onOpenBillingPortal}
                disabled={billingPortalLoading}
                startIcon={billingPortalLoading ? <CircularProgress size={16} color="inherit" /> : <CreditCardIcon sx={{ fontSize: 18 }} />}
                sx={checkoutBlockReasons.has(blockReason) ? {
                  px: 2,
                  py: 0.9,
                  borderRadius: BRAND_RADIUS.button,
                  borderColor: BRAND.line,
                  color: BRAND.text,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    borderColor: BRAND.accent,
                    bgcolor: brandAlpha('accent', 0.06),
                  },
                } : { px: 2, py: 0.9, whiteSpace: 'nowrap' }}
              >
                {billingPortalLoading ? t('stores.billing.opening') : t('stores.billing.action')}
              </Button>
            ) : null}

            <Button
              size="small"
              variant="outlined"
              onClick={onRefresh}
              disabled={refreshing}
              startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
              sx={{
                px: 2,
                py: 0.9,
                borderRadius: BRAND_RADIUS.button,
                borderColor: BRAND.line,
                color: BRAND.text,
                fontWeight: 700,
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: BRAND.accent,
                  bgcolor: brandAlpha('accent', 0.06),
                },
              }}
            >
              {refreshing ? t('stores.access.checking') : t('stores.access.refresh')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default StoreAccessBlockedBanner;
